import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

import { useArticle } from '@/lib/api/hooks';
import { track } from '@/lib/analytics';
import type { Article, ArticleBlock } from '@/types/api';

/**
 * ArticleDetail — single article view for "Understanding {product}"
 * content. Backend assembles the article from Sanity product data, so
 * each marketplace SKU automatically gets a page; nothing to maintain
 * here beyond the layout.
 */
export function ArticleDetail() {
  const { slug } = useParams();
  const { data: article, isLoading, error } = useArticle(slug);

  useEffect(() => {
    if (article?.slug) track('article_viewed', { slug: article.slug });
  }, [article?.slug]);

  if (isLoading) {
    return (
      <div className="h-[40rem] bg-[var(--surface-sunken)] rounded-md animate-pulse" />
    );
  }

  // Back target keeps the Learn tab the reader came from — articles live
  // on the Articles tab, so returning there (not the default Guides tab)
  // preserves their place in the list.
  const backTo = '/app/learn?tab=articles';

  if (error || !article) {
    return (
      <div className="max-w-form flex flex-col gap-15">
        <Link
          to={backTo}
          className="inline-flex items-center gap-5 type-label-sm text-ink hover:text-ink-secondary self-start"
        >
          <ChevronLeft size={14} strokeWidth={1.5} aria-hidden /> BACK TO LEARN
        </Link>
        <div className="type-body-md text-ink-secondary">
          Couldn&apos;t load this article.{' '}
          {(error as { message?: string } | null)?.message}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-25 max-w-prose">
      <div className="flex w-full px-15 md:px-20 bg-[var(--surface-sunken)] py-15">
        <Link
          to={backTo}
          className="inline-flex items-center gap-5 type-label-sm text-ink hover:text-ink-secondary self-start"
        >
          <ChevronLeft size={14} strokeWidth={1.5} aria-hidden /> BACK TO LEARN
        </Link>
      </div>
      <header className="flex flex-col gap-10 px-15 md:px-20">
        <h1 className="font-mono text-24 md:text-36 leading-105 tracking-tight uppercase">
          {article.title}
        </h1>
        {article.snippet && (
          <p className="type-body-md text-ink-secondary max-w-[60rem]">
            {article.snippet}
          </p>
        )}
      </header>

      <ArticleBody article={article} />
    </div>
  );
}

/** Shared reader body keeps the clinician preview identical to the article. */
export function ArticleBody({ article }: { article: Article }) {
  return (
    <article className="flex flex-col gap-25 px-15 md:px-20">
      {article.blocks?.length ? (
        <div className="flex flex-col gap-15">
          {article.blocks.map((b, i) => (
            <BlockRenderer key={i} block={b} />
          ))}
        </div>
      ) : (
        article.sections.map((s, i) => (
          <section key={i} className="flex flex-col gap-8">
            {s.heading && (
              <h2 className="type-label-md text-ink-secondary">
                {s.heading.toUpperCase()}
              </h2>
            )}
            <p className="type-body-md text-ink whitespace-pre-line leading-relaxed">
              {s.body}
            </p>
          </section>
        ))
      )}
    </article>
  );
}

function BlockRenderer({ block }: { block: ArticleBlock }) {
  switch (block.type) {
    case 'heading':
      return (
        <h2 className="type-label-md text-ink-secondary mt-10">
          {(block.text ?? '').toUpperCase()}
        </h2>
      );
    case 'subheading':
      return (
        <h3 className="type-label-sm text-ink-secondary mt-5">
          {(block.text ?? '').toUpperCase()}
        </h3>
      );
    case 'quote':
      return (
        <blockquote className="border-l-2 border-line-subtle pl-15 type-body-md text-ink-secondary italic leading-relaxed">
          {block.text}
        </blockquote>
      );
    case 'bullets':
    case 'numbered': {
      const ListTag = block.type === 'numbered' ? 'ol' : 'ul';
      return (
        <ListTag className={cnList(block.type)}>
          {(block.items ?? []).map((item, i) => (
            <li key={i} className="type-body-md text-ink leading-relaxed">
              {item}
            </li>
          ))}
        </ListTag>
      );
    }
    case 'image':
      return (
        <figure className="flex flex-col gap-5">
          <img
            src={block.url ?? undefined}
            alt={block.caption ?? ''}
            className="w-full bg-[var(--surface-sunken)]"
            loading="lazy"
          />
          {block.caption && (
            <figcaption className="type-body-sm text-ink-muted">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    case 'video':
      return (
        <video
          src={block.url ?? undefined}
          poster={block.poster_url ?? undefined}
          controls
          playsInline
          preload="metadata"
          className="w-full bg-[var(--surface-sunken)]"
        />
      );
    case 'paragraph':
    default:
      return (
        <p className="type-body-md text-ink whitespace-pre-line leading-relaxed">
          {block.text}
        </p>
      );
  }
}

function cnList(type: 'bullets' | 'numbered'): string {
  return type === 'numbered'
    ? 'list-decimal pl-20 flex flex-col gap-5'
    : 'list-disc pl-20 flex flex-col gap-5';
}
